import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Search, Grid, List, Hammer } from 'lucide-react';
import * as Icons from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CategoryBreadcrumbs from '@/components/CategoryBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ProductCard from '@/components/ProductCard';
import FilterSidebar from '@/components/FilterSidebar';
import { ImageManager } from '@/lib/imageManager';
import { isOutOfStock } from '@/lib/inventoryUtils';


import { useDebounce } from '@/hooks/useDebounce';

const CategoryDetail = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [viewMode, setViewMode] = useState<'grid' | 'list'>((searchParams.get('view') as 'grid' | 'list') || 'grid');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [subCategory, setSubCategory] = useState(searchParams.get('sub') || '');
  const [priceRange, setPriceRange] = useState(searchParams.get('price') || '');
  const [minQuantity, setMinQuantity] = useState(searchParams.get('minQty') || '');
  const debouncedMinQuantity = useDebounce(minQuantity, 500); // 500ms delay
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');

  // Parse array params (comma separated)
  const [selectedMaterial, setSelectedMaterial] = useState<string[]>(
    searchParams.get('materials') ? searchParams.get('materials')!.split(',') : []
  );
  const [selectedColorFamily, setSelectedColorFamily] = useState<string[]>(
    searchParams.get('colors') ? searchParams.get('colors')!.split(',') : []
  );
  const [selectedFinish, setSelectedFinish] = useState<string[]>(
    searchParams.get('finishes') ? searchParams.get('finishes')!.split(',') : []
  );
  // Vendor location filter removed as per request
  const [selectedSaleType, setSelectedSaleType] = useState<string[]>(
    searchParams.get('saleTypes') ? searchParams.get('saleTypes')!.split(',') : []
  );
  const [selectedGrades, setSelectedGrades] = useState<string[]>(
    searchParams.get('grades') ? searchParams.get('grades')!.split(',') : []
  );

  const [products, setProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableFilters, setAvailableFilters] = useState<{
    materials: string[];
    colorFamilies: string[];
    finishes: string[];
    // vendorLocations removed
    saleTypes: string[];
    grades: string[];
  }>({
    materials: [],
    colorFamilies: [],
    finishes: [],
    // vendorLocations removed
    saleTypes: [],
    grades: []
  });

  useEffect(() => {
    // Scroll to top when component mounts or category changes
    // Only scroll if we are not navigating back (presence of state usually implies back navigation but here we want to keep position if possible? 
    // Actually, usually "back" restores scroll position automatically by browser. 
    // The "scrollTo(0, 0)" here might force it to top. 
    // For now, I'll keep it but typically removing it helps "back" behavior.
    // Let's remove it to respect browser back scroll restoration if desired, 
    // but the user requirement is mainly about filters. 
    // I will leave it to avoid changing scroll behavior unless requested.
    window.scrollTo(0, 0);
    fetchCategoryData();
  }, [categoryName]);

  // Update URL params when filters change
  useEffect(() => {
    const params: any = {};

    if (viewMode !== 'grid') params.view = viewMode;
    if (searchQuery) params.q = searchQuery;
    if (subCategory && subCategory !== 'all') params.sub = subCategory;
    if (priceRange && priceRange !== 'all') params.price = priceRange;
    if (minQuantity) params.minQty = minQuantity;
    if (sortBy && sortBy !== 'newest') params.sort = sortBy;

    if (selectedMaterial.length > 0) params.materials = selectedMaterial.join(',');
    if (selectedColorFamily.length > 0) params.colors = selectedColorFamily.join(',');
    if (selectedFinish.length > 0) params.finishes = selectedFinish.join(',');
    // Vendor location param removed
    if (selectedSaleType.length > 0) params.saleTypes = selectedSaleType.join(',');
    if (selectedGrades.length > 0) params.grades = selectedGrades.join(',');

    setSearchParams(params, { replace: true });
  }, [
    viewMode,
    searchQuery,
    subCategory,
    priceRange,
    minQuantity,
    sortBy,
    selectedMaterial,
    selectedColorFamily,
    selectedFinish,
    // Vendor location dependency removed
    selectedSaleType,
    selectedGrades,
    setSearchParams
  ]);

  const fetchCategoryData = async () => {
    try {
      if (!categoryName) {
        console.log('No categoryName in URL params');
        setLoading(false);
        return;
      }

      // Decode the category name from URL
      const decodedCategoryName = decodeURIComponent(categoryName);
      console.log('Fetching category with name:', decodedCategoryName);

      // Fetch category by name
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('name', decodedCategoryName)
        .maybeSingle();

      if (categoryError) {
        console.error('Error fetching category:', categoryError);
        setCategory(null);
        setProducts([]);
        setSubcategories([]);
        setLoading(false);
        return;
      }

      if (!categoryData) {
        console.log('No category found for name:', decodedCategoryName);
        setCategory(null);
        setProducts([]);
        setSubcategories([]);
        setLoading(false);
        return;
      }

      console.log('Category found:', categoryData);

      // Check if category is coming soon
      if (categoryData.coming_soon) {
        setCategory(categoryData);
        setProducts([]);
        setFilteredProducts([]);
        setSubcategories([]);
        setLoading(false);
        return;
      }

      // Fetch products for this category using secure function
      const { data: productsData, error: productsError } = await supabase
        .rpc('get_products_by_category', { category_uuid: categoryData.id });

      if (productsError) {
        console.error('Error fetching products:', productsError);
        setProducts([]);
        setFilteredProducts([]);
      } else {
        // Transform products to include category data
        const productsWithCategories = (productsData || []).map(product => ({
          ...product,
          categories: { name: categoryData.name },
          subcategories: { name: 'ללא תת-קטגוריה' }
        }));
        setProducts(productsWithCategories);
        // Initial filtering will happen in the subsequent useEffect
        // but we can set it initially to productsWithCategories to avoid flash of empty if desired
        // though the useEffect will run immediately after render.
        setFilteredProducts(productsWithCategories);

        // Extract available filter options and filter out empty/null values
        const materials = Array.from(new Set(productsWithCategories.map(p => p.material).filter(val => val && val.trim() !== '')));
        const colorFamilies = Array.from(new Set(productsWithCategories.map(p => p.color_family).filter(val => val && val.trim() !== '')));
        const finishes = Array.from(new Set(productsWithCategories.map(p => p.finish).filter(val => val && val.trim() !== '')));
        // Vendor locations extraction removed
        const saleTypes = Array.from(new Set(productsWithCategories.map(p => p.sale_type).filter(val => val && val.trim() !== '')));
        const grades = Array.from(new Set(productsWithCategories.map(p => p.grade).filter(val => val && val.trim() !== '')));

        setAvailableFilters({
          materials,
          colorFamilies,
          finishes,
          // vendorLocations removed
          saleTypes,
          grades
        });

        // Track view_item_list event for category
        if (productsWithCategories.length > 0 && typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'view_item_list', {
            item_list_name: categoryData.name,
            items: productsWithCategories.slice(0, 10).map((product, index) => ({
              item_id: product.id,
              item_name: product.name,
              item_category: categoryData.name,
              price: product.price_per_unit,
              index: index
            }))
          });
        }
      }

      // Fetch subcategories for this category
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', categoryData.id);

      if (subcategoriesError) {
        console.error('Error fetching subcategories:', subcategoriesError);
        setSubcategories([]);
      } else {
        setSubcategories(subcategoriesData || []);
      }

      setCategory(categoryData);
    } catch (error) {
      console.error('Error fetching category data:', error);
      setCategory(null);
      setProducts([]);
      setSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort products whenever filters change
  useEffect(() => {
    const applyFilters = () => {
      if (!products || products.length === 0 || !Array.isArray(products)) {
        setFilteredProducts([]);
        return;
      }

      let filtered = products.filter(product => product && typeof product === 'object');

      // Apply search filter
      if (searchQuery && searchQuery.trim() !== '') {
        filtered = filtered.filter(product => {
          if (!product) return false;
          const name = product.name || '';
          const description = product.description || '';
          return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            description.toLowerCase().includes(searchQuery.toLowerCase());
        });
      }

      // Apply subcategory filter
      if (subCategory && subCategory !== 'all' && subcategories && subcategories.length > 0) {
        filtered = filtered.filter(product => {
          if (!product || !product.subcategory_id) return false;
          const subcategory = subcategories.find(sub => sub && sub.id === product.subcategory_id);
          return subcategory && subcategory.name === subCategory;
        });
      }

      // Apply material filter
      if (selectedMaterial && selectedMaterial.length > 0) {
        filtered = filtered.filter(product => product && selectedMaterial.includes(product.material));
      }

      // Apply color family filter
      if (selectedColorFamily && selectedColorFamily.length > 0) {
        filtered = filtered.filter(product => product && selectedColorFamily.includes(product.color_family));
      }

      // Apply finish filter
      if (selectedFinish && selectedFinish.length > 0) {
        filtered = filtered.filter(product => product && selectedFinish.includes(product.finish));
      }

      // Vendor location filter removed

      // Apply sale type filter
      if (selectedSaleType && selectedSaleType.length > 0) {
        filtered = filtered.filter(product => product && selectedSaleType.includes(product.sale_type));
      }

      // Apply grade filter
      if (selectedGrades && selectedGrades.length > 0) {
        filtered = filtered.filter(product => product && selectedGrades.includes(product.grade));
      }

      // Apply price range filter
      if (priceRange && priceRange !== 'all') {
        filtered = filtered.filter(product => {
          const price = product?.price_per_unit;
          if (typeof price !== 'number') return false;
          switch (priceRange) {
            case '0-50':
              return price >= 0 && price <= 50;
            case '50-100':
              return price > 50 && price <= 100;
            case '100-200':
              return price > 100 && price <= 200;
            case '200+':
              return price > 200;
            default:
              return true;
          }
        });
      }

      // Apply quantity filter (using debounced value)
      if (debouncedMinQuantity && debouncedMinQuantity !== '') {
        const minQty = parseFloat(debouncedMinQuantity);
        if (!isNaN(minQty)) {
          filtered = filtered.filter(product => {
            const isPackage = product?.measurement_unit === 'package_by_meter' || product?.measurement_unit === 'package_by_square_meter';
            const coverage = product?.package_coverage_per_unit || 1;

            // For packages, calculate total coverage (sm/m), otherwise use direct inventory
            const availableQuantity = isPackage
              ? (product.total_inventory * coverage)
              : (product.total_inventory || 0);

            const maxOrder = product?.max_order_quantity;

            // Check if inventory is sufficient
            if (availableQuantity < minQty) return false;

            // Check if max order setting permits this quantity
            // If maxOrder is defined, it's typically in the base unit (packages or units)
            // but for filtering purposes, we mainly care about if the requested amount is available.
            return true;
          });
        }
      }

      // Apply sorting
      if (filtered && filtered.length > 0) {
        filtered.sort((a, b) => {
          // 1. Primary Sort: Out of Stock always at bottom
          const aOutOfStock = isOutOfStock(a);
          const bOutOfStock = isOutOfStock(b);

          if (aOutOfStock && !bOutOfStock) return 1;
          if (!aOutOfStock && bOutOfStock) return -1;

          // 2. Secondary Sort: User selection
          switch (sortBy) {
            case 'price-low':
              return (a.price_per_unit || 0) - (b.price_per_unit || 0);
            case 'price-high':
              return (b.price_per_unit || 0) - (a.price_per_unit || 0);
            case 'newest':
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA;
            default:
              return 0;
          }
        });
      }

      setFilteredProducts(filtered || []);
    };

    applyFilters();
  }, [products, searchQuery, subCategory, selectedMaterial, selectedColorFamily, selectedFinish, selectedSaleType, selectedGrades, priceRange, debouncedMinQuantity, sortBy]);

  // Early return if still loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-20 lg:pt-8 pb-12">
          <div className="container mx-auto px-4">
            <div className="h-64 bg-muted animate-pulse rounded-xl mb-8"></div>
            <div className="flex gap-8">
              <div className="hidden lg:block lg:w-80 bg-muted animate-pulse rounded-lg h-96"></div>
              <div className="flex-1">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="h-64 bg-muted animate-pulse rounded-lg"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Early return if no category found
  if (!loading && !category) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-20 lg:pt-8 pb-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold mb-4">קטגוריה לא נמצאה</h1>
            <p className="text-muted-foreground mb-6">לא הצלחנו למצוא את הקטגוריה המבוקשת</p>
            <Button onClick={() => navigate('/products')}>
              חזרה לעמוד הקטגוריות
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Handle coming soon categories
  if (!loading && category && category.coming_soon) {
    const getCategoryIcon = (iconName: string) => {
      if (iconName && (Icons as any)[iconName]) {
        return (Icons as any)[iconName];
      }
      return Hammer;
    };
    const IconComponent = getCategoryIcon(category.icon_name || '');

    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 lg:pt-8 pb-12">
          <div className="container mx-auto px-4">
            <CategoryBreadcrumbs />

            <div className="text-center max-w-2xl mx-auto mt-12">
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-8 mb-8">
                <Badge className="bg-amber-500 text-white mb-4">בקרוב</Badge>

                {category.image_url ? (
                  <img
                    src={ImageManager.getImageUrl(category.image_url, 'category')}
                    alt={category.name}
                    className="w-32 h-32 object-cover rounded-lg mx-auto mb-4 grayscale"
                    onError={(e) => ImageManager.handleImageError(e, 'category')}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mx-auto mb-4 grayscale">
                    <IconComponent className="h-16 w-16 text-white" />
                  </div>
                )}

                <h1 className="text-3xl font-bold mb-4">{category.name}</h1>
                {category.description && (
                  <p className="text-lg text-muted-foreground mb-6">
                    {category.description}
                  </p>
                )}
                <p className="text-muted-foreground mb-8">
                  אנחנו עובדים על הוספת מוצרים איכותיים לקטגוריה זו. חזור בקרוב כדי לגלות את המבחר שלנו!
                </p>
                <Button onClick={() => navigate('/products')}>
                  חזרה לעמוד הקטגוריות
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-4 lg:pt-8 pb-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumbs - Memoized component that never re-renders */}
          <CategoryBreadcrumbs />

          {/* Category Header */}
          <div className="relative h-64 rounded-xl overflow-hidden mb-8">
            <img
              src={ImageManager.getImageUrl(category?.image_url || '', 'category')}
              alt={category?.name || 'קטגוריה'}
              className="w-full h-full object-cover"
              onError={(e) => ImageManager.handleImageError(e, 'category')}
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="flex items-center justify-center gap-3 mb-4">
                  {(() => {
                    if (category?.icon_name) {
                      const IconComponent = (Icons as any)[category.icon_name];
                      if (IconComponent) return <IconComponent className="h-12 w-12" />;
                    }
                    return <Hammer className="h-12 w-12" />;
                  })()}
                  <h1 className="text-4xl font-bold">{category?.name || 'קטגוריה'}</h1>
                </div>
                <p className="text-xl mb-2">{category?.description || 'מגוון מוצרים איכותיים'}</p>
                <Badge className="bg-lime text-lime-foreground">
                  {products?.length || 0} מוצרים במלאי
                </Badge>
              </div>
            </div>
          </div>

          {/* SEO Description */}
          {category?.seo_description && (
            <div className="bg-card rounded-xl p-6 mb-8 border border-border">
              <p className="text-muted-foreground leading-relaxed text-lg">{category.seo_description}</p>
            </div>
          )}

          {/* Top Filters Layout */}
          <div className="flex flex-col gap-2">
            <FilterSidebar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              subCategory={subCategory}
              setSubCategory={setSubCategory}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              selectedMaterial={selectedMaterial}
              setSelectedMaterial={setSelectedMaterial}
              selectedColorFamily={selectedColorFamily}
              setSelectedColorFamily={setSelectedColorFamily}
              selectedFinish={selectedFinish}
              setSelectedFinish={setSelectedFinish}
              selectedSaleType={selectedSaleType}
              setSelectedSaleType={setSelectedSaleType}
              selectedGrades={selectedGrades}
              setSelectedGrades={setSelectedGrades}
              subcategories={subcategories}
              availableFilters={availableFilters}
              categoryName={category?.name}
              resultsCount={filteredProducts?.length || 0}
              minQuantity={minQuantity}
              setMinQuantity={setMinQuantity}
            />

            {/* Products Content */}
            <div className="flex-1">
              {/* View Mode Toggle and Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-muted-foreground">
                  {filteredProducts?.length || 0} מוצרים נמצאו
                </p>

                <div className="hidden lg:flex items-center gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Products Grid/List */}
              {filteredProducts && filteredProducts.length > 0 ? (
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-4'
                }>
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      viewMode={viewMode}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mb-4">
                    <Search className="h-12 w-12 text-muted-foreground mx-auto" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">לא נמצאו תוצאות</h3>
                  <p className="text-muted-foreground mb-4">
                    נסה לשנות את קריטריוני החיפוש או הסינון
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSubCategory('');
                      setPriceRange('');
                      setMinQuantity('');
                      setSelectedMaterial([]);
                      setSelectedColorFamily([]);
                      setSelectedFinish([]);
                      setSelectedFinish([]);
                      // Vendor location reset removed
                      setSelectedSaleType([]);
                      setSelectedGrades([]);
                    }}
                  >
                    נקה סינונים
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryDetail;